/**
 * Named filesystem-mode presets (G-B) — thin factories that compose existing primitives into a
 * `Partial<AgentOptions>`, so a consumer can pick a posture without hand-wiring the fs/tools:
 *
 *   new Agent(sandboxAgentOptions({ ai }))        // in-memory VFS — real disk untouched
 *   new Agent(diskAgentOptions({ ai }))           // jailed real disk @ cwd (the default)
 *   new Agent(await fullAgentOptions({ ai }))     // disk + a real /bin/sh tool (run binaries)
 *
 * The core stays unopinionated (IoC): these just preset `fs`/`tools`. `sandbox`/`disk` are edge-safe to
 * import; `full` is node-only (real shell) and DYNAMIC-imports it, so importing this module never pulls
 * `node:child_process` into an edge/browser bundle.
 */
import { MemFilesystem } from '@livx.cc/wcli/core';
import type { AgentOptions } from './Agent';
import { defaultTools, type AgentTool } from './tools';

/** Sandbox mode: an in-memory VFS — the real disk is never read or written. Edge/browser/test/dry-run. */
export function sandboxAgentOptions(opts: Partial<AgentOptions> = {}): Partial<AgentOptions> {
  return { fs: new MemFilesystem(), ...opts };
}

/** Disk mode (the default): operate on the real filesystem, jailed to cwd (secrets hidden by DEFAULT_DENY).
 *  Omitting `fs` lets the Agent lazily resolve the jailed disk — this preset just makes the intent explicit
 *  (and is where you'd thread overrides). Pass an explicit `fs` to use a different backend. */
export function diskAgentOptions(opts: Partial<AgentOptions> = {}): Partial<AgentOptions> {
  return { ...opts }; // fs omitted → Agent.ensureFs() resolves Jailed(NodeDisk(cwd))
}

/** Full mode: disk + a real `/bin/sh` tool (run installed binaries — git, bun, node, …) plus its
 *  background-job companions. NODE-ONLY: the real-shell module is dynamic-imported so this file stays
 *  edge-safe to import. `cwd` defaults to `process.cwd()`; secret env is scrubbed from spawned children. */
export async function fullAgentOptions(opts: Partial<AgentOptions> & { cwd?: string } = {}): Promise<Partial<AgentOptions>> {
  const { makeRealShellTool, makeShellJobTools, ShellJobRegistry } = await import('./tools.shell');
  const { cwd = process.cwd(), tools, ...rest } = opts;
  const registry = new ShellJobRegistry({ cwd, killOnExit: true });
  const shell: AgentTool[] = [makeRealShellTool({ cwd, registry }), ...makeShellJobTools(registry)];
  return { ...rest, tools: [...(tools ?? defaultTools()), ...shell] };
}
